// API utilities for request debouncing and batching

// Debounce utility
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Request cache to prevent duplicate requests
const requestCache = new Map();
const cacheTimeout = 30000; // 30 seconds

// Cache key generator
const getCacheKey = (url, params = {}) => {
  return `${url}-${JSON.stringify(params)}`;
};

// Cached request utility
export const cachedRequest = async (requestFn, url, params = {}) => {
  const cacheKey = getCacheKey(url, params);
  const now = Date.now();
  
  // Check if we have a valid cached response
  if (requestCache.has(cacheKey)) {
    const cached = requestCache.get(cacheKey);
    if (now - cached.timestamp < cacheTimeout) {
      return cached.data;
    }
  }
  
  // Make the request
  const data = await requestFn();
  
  // Cache the response
  requestCache.set(cacheKey, {
    data,
    timestamp: now
  });
  
  return data;
};

// Batch similar requests
const pendingRequests = new Map();

export const batchedRequest = async (requestFn, key) => {
  // If there's already a pending request for this key, wait for it
  if (pendingRequests.has(key)) {
    return pendingRequests.get(key);
  }
  
  // Create a new promise for this request
  const promise = requestFn().finally(() => {
    pendingRequests.delete(key);
  });
  
  pendingRequests.set(key, promise);
  return promise;
};

// Clear cache utility
export const clearCache = () => {
  requestCache.clear();
};

// Clear specific cache entry
export const clearCacheEntry = (url, params = {}) => {
  const cacheKey = getCacheKey(url, params);
  requestCache.delete(cacheKey);
};

